import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  IconVisibility,
  IconVisibilityOff,
  Toast,
  TOAST_VARIANTS,
  TOAST_POSITIONS,
  TOAST_VISIBILITY_DURATIONS,
  Dialog,
  BUTTON_VARIANTS,
  CONTENT_TYPES,
  Content,
} from 'cdk-radial';
import APIIntegrationsTable from './APIIntegrationsTable';
import APIIntegrationsTableV2 from './APIIntegrationsTableV2';
import './APIs.css';
import {
  generateNewSecret,
  getOrders,
  getOrderAPIConsents,
  getSubscriptionCounts,
  resetState,
} from '../../../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import { resetDaabSubscriptionsState } from '../../../../../redux/connectedComponent/daabSubscriptions/daabSubscriptions.slice';
import { Constants } from '../../../../../constants/constants';

import { InlineToastNotification } from './InlineToastNotification';
import { AppDetailsContainer } from './AppDetailsContainer';
import { LoaderToast } from './InlineToastNotification';
import { resetunSubscribeRequestSentFlag } from '../../../../../redux/connectedComponent/daabSubscriptions/daabSubscriptions.slice';
import { resetCreateandUpdateOrderFlags } from '../../../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import { hasPendingCertifiedApi } from '../../../../../utils/utils';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { trackPurchaseCompleted, PURCHASE_STATUS } from '../../../../../utils/gtm';

export const APISecret = ({ secret }) => {
  const hiddenSecret = '•••••••••••••••••••••••••••';
  const [showSecret, setShowSecret] = useState(false);
  const handleClick = () => {
    setShowSecret(!showSecret);
  };
  return (
    <>
      <div>{showSecret ? secret : hiddenSecret}</div>
      {showSecret ? (
        <IconVisibilityOff
          data-testid="hide-secret"
          className="api-details-icons"
          onClick={handleClick}
        />
      ) : (
        <IconVisibility
          data-testid="show-secret"
          className="api-details-icons"
          onClick={handleClick}
        />
      )}
    </>
  );
};

export const APIs = (props) => {
  const state = useSelector((state) => state);
  const dispatch = useDispatch();
  const {
    orders,
    lastFetched,
    errorInProcess: errorFetchingOrders,
    generatingSecret: generatingNewSecret,
    orderCreated,
    orderUpdated,
    fromPurchaseFlow,
    fetchingOrderAPIConsents,
    fetchingOrderDetails,
    apiDeleted,
    bundleDeleted,
    deletingApi,
    deletingBundle,
  } = state?.orderAPISlice;
  const [showToast, setShowToast] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [toastContent, setToastContent] = useState(false);
  const [apiCount, setApiCount] = useState(0);
  const [polling, setPolling] = useState(false);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [apiAddedToTheOrder, setApiAddedToTheOrder] = useState(false);
  const [toastDismissed, setToastDismissed] = useState(false);
  const [storedPricingUpdate, setStoredPricingUpdate] = useState(null);
  const [pendingPricingToast, setPendingPricingToast] = useState(null);
  const [isBundleNewApisFrameDismissed, setIsBundleNewApisFrameDismissed] =
    useState(false);

  const {
    app = {},
    restApis = [],
    asyncApis = [],
    bundleIntegrations = [],
  } = orders;

  const appId = orders?.app?.id;
  const {
    fortellisEnterpriseEntityId,
    authenticationToken: token,
    isBundlePricingEnabled,
  } = state?.loginDetailsSlice;
  const { removeLocationsApiName, unSubscribeRequestSent } =
    state?.daabSubscriptionsSlice;

  const options = {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  };
  const isLoadingData =
    polling ||
    fetchingOrderAPIConsents ||
    fetchingOrderDetails;

  const shouldShowNoApiMessage =
    !app?.apiKey && !errorFetchingOrders && ordersLoaded && !polling;

  const shouldShowLoader = isLoadingData && !shouldShowNoApiMessage;

  const orderHasPendingCertifiedApi = useMemo(
    () => hasPendingCertifiedApi([...restApis, ...asyncApis]),
    [restApis, asyncApis],
  );

  const bundleNewApiSummary = useMemo(() => {
    if (!Array.isArray(bundleIntegrations) || bundleIntegrations.length === 0) {
      return null;
    }

    const bundlesWithNewApis = bundleIntegrations
      .map((bundle) => {
        const newApiCount = (bundle?.apis || []).filter((bundleApi) =>
          Boolean(bundleApi?.isNewApi)
        ).length;

        return {
          id: bundle?.id,
          bundleName: bundle?.name || 'bundle',
          newApiCount,
        };
      })
      .filter((bundle) => bundle.newApiCount > 0);

    if (bundlesWithNewApis.length === 0) return null;

    if (bundlesWithNewApis.length > 1) {
      return {
        message:
          'New APIs have been added to multiple bundles and are ready for configuration.',
      };
    }

    const singleBundle = bundlesWithNewApis[0];
    if (singleBundle.newApiCount === 1) {
      return {
        message: `A new API has been added to ${singleBundle.bundleName} and is ready for configuration.`,
      };
    }

    return {
      message: `${singleBundle.newApiCount} new APIs have been added to ${singleBundle.bundleName} and is ready for configuration.`,
    };
  }, [bundleIntegrations]);

  const isOrderNotEmpty = useMemo(() => {
    return Boolean(
      app?.id ||
      (restApis && restApis.length > 0) ||
      (asyncApis && asyncApis.length > 0) ||
      (bundleIntegrations && bundleIntegrations.length > 0)
    );
  }, [app?.id, restApis, asyncApis, bundleIntegrations]);

  const shouldShowBundleNewApisFrame =
    Boolean(bundleNewApiSummary) &&
    Boolean(lastFetched) &&
    !fetchingOrderDetails &&
    app?.status !== Constants.APP_STATUSES.PROVISIONING && app?.status !== Constants.APP_STATUSES.ERROR &&
    !errorFetchingOrders &&
    isOrderNotEmpty &&
    !isBundleNewApisFrameDismissed;

  const shouldShowApiAddedToast =
    apiAddedToTheOrder &&
    (fromPurchaseFlow || isBundlePricingEnabled) &&
    !polling &&
    !fetchingOrderAPIConsents &&
    !fetchingOrderDetails &&
    !apiDeleted &&
    !bundleDeleted &&
    !deletingApi &&
    !deletingBundle &&
    (restApis.length > 0 || asyncApis.length > 0 || bundleIntegrations.length > 0);

  useEffect(() => {
    if (bundleNewApiSummary) {
      setIsBundleNewApisFrameDismissed(false);
    }
  }, [bundleNewApiSummary]);

  useEffect(() => {
    if (apiDeleted || bundleDeleted || deletingApi || deletingBundle) {
      setApiAddedToTheOrder(false);
    }
  }, [apiDeleted, bundleDeleted, deletingApi, deletingBundle]);

  const dialogContent = (
    <Content type={CONTENT_TYPES.BODY_2}>
      You are creating a new API secret. Do you want to continue?
    </Content>
  );

  useEffect(() => {
    dispatch(resetDaabSubscriptionsState());

    // Check for pricing plan update message from sessionStorage (persists across reload)
    const storedMessage = sessionStorage.getItem('pricingPlanUpdated');
    if (storedMessage) {
      try {
        const pricingData = JSON.parse(storedMessage);
        // Store in pending state until data is loaded
        setPendingPricingToast(pricingData);
        sessionStorage.removeItem('pricingPlanUpdated');
      } catch (e) {
        sessionStorage.removeItem('pricingPlanUpdated');
      }
    }

    return () => {
      dispatch(resetCreateandUpdateOrderFlags());
      dispatch(resetunSubscribeRequestSentFlag());
    };
  }, [dispatch]);

  // Show pricing update toast after all data is loaded
  useEffect(() => {
    const isDataReady =
      ordersLoaded &&
      !fetchingOrderDetails &&
      !polling;

    if (isDataReady && pendingPricingToast && !storedPricingUpdate) {
      setStoredPricingUpdate(pendingPricingToast);
      setPendingPricingToast(null);
    }
  }, [
    ordersLoaded,
    fetchingOrderDetails,
    polling,
    pendingPricingToast,
    storedPricingUpdate,
  ]);

  // Fetch order details if needed on component mount or when order is updated/created
  useEffect(() => {
    if (fortellisEnterpriseEntityId) {
      if (
        !appId ||
        orderCreated ||
        orderUpdated ||
        app?.status === Constants.APP_STATUSES.PROVISIONING
      ) {
        dispatch(
          getOrders({ token, entityId: fortellisEnterpriseEntityId }),
        ).then(() => setOrdersLoaded(true));
      }
    }
  }, [
    appId,
    fortellisEnterpriseEntityId,
    dispatch,
    token,
    orderCreated,
    orderUpdated,
    app?.status,
  ]);

  // When the app is registered, fetch subscription data, system info, and API consents
  useEffect(() => {
    const appId = orders?.app?.id;
    // Ensure all necessary data is fetched before proceeding with further actions
    if (
      appId &&
      token &&
      orders?.app?.status === Constants.APP_STATUSES.REGISTERED &&
      !fetchingOrderDetails &&
      !errorFetchingOrders
    ) {
      dispatch(getOrderAPIConsents({ appId, token }));
    }
  }, [
    dispatch,
    orders?.app?.id,
    orders?.app?.status,
    token,
    fetchingOrderDetails,
    errorFetchingOrders,
  ]);

  useEffect(() => {
    const appId = orders?.app?.id;
    const appStatus = orders?.app?.status;
    if (
      appId &&
      ordersLoaded &&
      token &&
      !fetchingOrderDetails &&
      !errorFetchingOrders &&
      appStatus === Constants.APP_STATUSES.REGISTERED
    ) {
      dispatch(getSubscriptionCounts({ token, appId }));
    }
  }, [
    orders?.app?.id,
    orders?.app?.status,
    ordersLoaded,
    token,
    fetchingOrderDetails,
    errorFetchingOrders,
    dispatch,
  ]);

  // Start/stop polling based on app provisioning status
  useEffect(() => {
    if (!lastFetched) {
      setPolling(false); // fallback safety
      return;
    }
    if (orderCreated) {
      //Order was just created — starting polling
      setPolling(true);
    } else if (app?.status === Constants.APP_STATUSES.PROVISIONING) {
      //App is provisioning — starting polling
      setPolling(true);
    } else {
      //App is not provisioning and order not just created — not polling
      setPolling(false);
    }
  }, [lastFetched, app?.status, orderCreated]);

  // Start polling for orders while the app is in the "PROVISIONING" state
  useEffect(() => {
    let intervalId;

    if (
      polling &&
      ordersLoaded &&
      (!app?.status || app?.status === Constants.APP_STATUSES.PROVISIONING)
    ) {
      intervalId = setInterval(() => {
        dispatch(getOrders({ token, entityId: fortellisEnterpriseEntityId }));
      }, Constants.APP_REFRESH_INTERVAL_MS);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    polling,
    ordersLoaded,
    app?.status,
    token,
    dispatch,
    fortellisEnterpriseEntityId,
  ]);

  //When provisioning completes and app becomes "REGISTERED" or fails, stop polling.
  useEffect(() => {
    if (polling) {
      if (app?.status === Constants.APP_STATUSES.REGISTERED) {
        setApiAddedToTheOrder(true);
        setPolling(false);
      } else if (app?.status === Constants.APP_STATUSES.ERROR || app?.status === Constants.APP_STATUSES.PROVISIONING_FAILED) {
        setPolling(false);
      }
    }
  }, [polling, app?.status]);

  // Handle GTM purchase completed tracking (success/failure)
  useEffect(() => {
    if (ordersLoaded && app?.status) {
      const isCompleted = app?.status === Constants.APP_STATUSES.REGISTERED;
      const isFailed = app?.status === Constants.APP_STATUSES.ERROR || app?.status === Constants.APP_STATUSES.PROVISIONING_FAILED;
      
      if (isCompleted || isFailed) {
        const storedPurchase = sessionStorage.getItem('pendingPurchaseTracking');
        if (storedPurchase) {
          try {
            const { totalPrice, totalInstallFee, apiItemsSize, bundleCartSize } = JSON.parse(storedPurchase);
            trackPurchaseCompleted({
              orderId: app?.id,
              totalPrice,
              totalInstallFee,
              apiItemsSize,
              bundleCartSize,
              purchaseStatus: isCompleted ? PURCHASE_STATUS.SUCCESS : PURCHASE_STATUS.FAILURE
            });
          } catch (e) {
            console.error('Error tracking purchase completion:', e);
          } finally {
            sessionStorage.removeItem('pendingPurchaseTracking');
          }
        }
      }
    }
  }, [ordersLoaded, app?.status, app?.id]);

  // For bundle pricing: trigger toast when bundles are loaded and app is already registered
  useEffect(() => {
    if (
      isBundlePricingEnabled &&
      bundleIntegrations.length > 0 &&
      app?.status === Constants.APP_STATUSES.REGISTERED &&
      ordersLoaded &&
      !fetchingOrderAPIConsents &&
      !fetchingOrderDetails &&
      !polling &&
      !apiDeleted &&
      !bundleDeleted &&
      !deletingApi &&
      !deletingBundle &&
      !apiAddedToTheOrder &&
      !toastDismissed
    ) {
      setApiAddedToTheOrder(true);
    }
  }, [
    isBundlePricingEnabled,
    bundleIntegrations.length,
    app?.status,
    ordersLoaded,
    fetchingOrderAPIConsents,
    fetchingOrderDetails,
    polling,
    apiDeleted,
    bundleDeleted,
    deletingApi,
    deletingBundle,
    apiAddedToTheOrder,
    toastDismissed,
  ]);

  const keyIssueDate = app?.keyIssuedAt
    ? new Date(Number(app.keyIssuedAt))
        .toLocaleString('en-US', options)
        .replace(',', '') + ' CST'
    : '';
  const handleGenerateNewSecret = () => {
    setOpenDialog(true);
  };
  return (
    <>
      <LoaderToast
        visible={shouldShowLoader}
        loadingText={
          orderCreated || orderUpdated
            ? 'Your API order is updating and will be ready soon.'
            : 'Fetching Order Details.'
        }
      />
      <InlineToastNotification
        visible={shouldShowBundleNewApisFrame}
        message={bundleNewApiSummary?.message}
        variant={TOAST_VARIANTS.POSITIVE}
        onClose={() => setIsBundleNewApisFrameDismissed(true)}
      />
      <InlineToastNotification
        visible={shouldShowApiAddedToast}
        message={
          orderHasPendingCertifiedApi
            ? 'Your API Order is pending CDK review. You will be contacted to complete this process. Please reach out via the Dev Care Portal with any questions.'
            : 'Your API order is ready.'
        }
        variant={TOAST_VARIANTS.POSITIVE}
        onClose={() => {
          setApiAddedToTheOrder(false);
          setToastDismissed(true);
        }}
      />

      <InlineToastNotification
        visible={app?.status === 'Error'}
        message="An error occurred. Contact support for more information."
        variant={TOAST_VARIANTS.NEGATIVE}
        showCloseButton={false}
      />

      <InlineToastNotification
        visible={errorFetchingOrders && !shouldShowLoader}
        message="Unable to fetch the list of integrated APIs. Try again after some time."
        variant={TOAST_VARIANTS.NEGATIVE}
        showCloseButton={false}
      />

      <InlineToastNotification
        visible={shouldShowNoApiMessage && !shouldShowLoader}
        message="You don't have an API integration created. Add at least one API."
        variant={TOAST_VARIANTS.NEUTRAL}
        showCloseButton={false}
      />
      <>
        <div className={isLoadingData ? 'api_integrations_table_loading' : ''}>
          {
            <AppDetailsContainer
              apiKey={app?.apiKey}
              apiSecret={app?.apiSecret}
              keyIssueDate={keyIssueDate}
              onGenerateNewSecret={handleGenerateNewSecret}
              isGeneratingSecretDisabled={isLoadingData}
            />
          }
          <section>
            <div className="container-heading">
              {Constants.API_INTEGRATIONS_TITLE}
            </div>
            {isBundlePricingEnabled ? (
              <APIIntegrationsTableV2 isLoadingTableData={isLoadingData} />
            ) : (
              <APIIntegrationsTable
                isLoadingTableData={isLoadingData}
                setApiCount={setApiCount}
                token={token}
              />
            )}
          </section>
        </div>
      </>

      {showToast && (
        <Toast
          variant={TOAST_VARIANTS.POSITIVE}
          content={toastContent}
          id={'contact-provider-email-toast'}
          actions={[
            {
              onClick: () => {
                setShowToast(false);
              },
              text: 'Dismiss',
            },
          ]}
          onVisibilityDurationEnd={() => {
            setShowToast(false);
          }}
          position={TOAST_POSITIONS.FIXED}
          visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
          style={{ marginTop: '64px' }}
        />
      )}

      {storedPricingUpdate && (
        <Toast
          variant={TOAST_VARIANTS.POSITIVE}
          content={
            storedPricingUpdate?.isBundle ? (
              <div>
                {Constants.BUNDLE_PLAN_UPDATED}{' '}
                <strong>{storedPricingUpdate?.apiName}</strong>.
              </div>
            ) : (
              <div>
                {Constants.API_PLAN_UPDATED}{' '}
                <strong>{storedPricingUpdate?.apiName}</strong>.{' '}
                {Constants.BILLING_CYCLE}
              </div>
            )
          }
          id={'contact-provider-edit-plan-toast'}
          onVisibilityDurationEnd={() => {
            setShowToast(false);
            setStoredPricingUpdate(null);
          }}
          position={TOAST_POSITIONS.FIXED}
          visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
          style={{ marginTop: '64px' }}
        />
      )}
      {unSubscribeRequestSent && (
        <Toast
          variant={TOAST_VARIANTS.POSITIVE}
          content={`Your request for ${removeLocationsApiName} has been successful.`}
          id={'contact-provider-edit-plan-toast'}
          onVisibilityDurationEnd={() => {
            dispatch(resetunSubscribeRequestSentFlag());
          }}
          position={TOAST_POSITIONS.FIXED}
          visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
          style={{ marginTop: '64px' }}
        />
      )}
      {openDialog ? (
        <Dialog
          className="generate-api-secret-dialog"
          buttonsProps={[
            {
              id: 'cancel-action',
              onClick: () => setOpenDialog(false),
              text: 'Cancel',
              variant: BUTTON_VARIANTS.SECONDARY,
            },
            {
              id: 'Ok-action',
              onClick: () => {
                dispatch(generateNewSecret({ token, appId: app?.id }))
                  .then(() => {
                    dispatch(getOrders({ token }));
                    setOpenDialog(false);
                    setShowToast(true);
                    setToastContent(
                      Constants.NEW_SECRET_SUCCESSFULLY_GENERATED_MESSAGE,
                    );
                  })
                  .catch((error) => {
                    setOpenDialog(false);
                    console.error('Error generating new secret:', error);
                  });
              },
              isLoading: fetchingOrderDetails || generatingNewSecret === true,
              text: 'Ok',
              variant: BUTTON_VARIANTS.PRIMARY,
            },
          ]}
          id="generate-new-secret"
          dataTestId="generate-new-secret"
          isOpen={openDialog}
          onClose={() => setOpenDialog(false)}
          title="Confirm Generating Secret Key"
        >
          {dialogContent}
        </Dialog>
      ) : null}
    </>
  );
};
